import { faSpinner, faXmark } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/sharp-light-svg-icons";
import { useMutation, useQuery } from "@tanstack/react-query";
import { type ReactNode, useEffect, useRef, useState } from "react";
import {
    useAppSelector,
    useDatadownloadsApiClient,
    useEmissionRecordsApiClient,
} from "../../hooks";

interface EmissionPdfDownloadButtonProps {
    emissionId: string;
    className?: string;
}

const baseClasses =
    "inline-flex items-center gap-1.5 shrink-0 whitespace-nowrap rounded px-2 py-0.5 text-xs transition-colors disabled:opacity-50 disabled:cursor-not-allowed";

interface DismissiblePopupProps {
    onDismiss: () => void;
    tone?: "neutral" | "error";
    children: ReactNode;
}

const DismissiblePopup = ({
    onDismiss,
    tone = "neutral",
    children,
}: DismissiblePopupProps) => {
    const borderClass =
        tone === "error" ? "border-red-200" : "border-slate-200";
    const textClass = tone === "error" ? "text-red-600" : "text-slate-600";
    return (
        <div
            className={`absolute right-0 top-full z-50 mt-1 w-64 rounded-lg border ${borderClass} bg-white p-3 shadow-lg`}
        >
            <button
                type="button"
                aria-label="Dismiss"
                className="absolute right-1.5 top-1.5 text-slate-400 hover:text-slate-600"
                onClick={onDismiss}
            >
                <FontAwesomeIcon icon={faXmark} className="size-3" />
            </button>
            <div className={`pr-4 text-xs ${textClass}`}>{children}</div>
        </div>
    );
};

export const EmissionPdfDownloadButton = (
    props: EmissionPdfDownloadButtonProps,
) => {
    const userFlags = useAppSelector((s) => s.auth.flags);
    const userHasExportPermissions = useAppSelector(
        (s) => s.auth.permissions.emissionExport,
    );
    const emissionsApiClient = useEmissionRecordsApiClient();
    const downloadsApiClient = useDatadownloadsApiClient();

    const [dataDownloadId, setDataDownloadId] = useState<string | undefined>(
        undefined,
    );
    const [info, setInfo] = useState<string | undefined>(undefined);
    const [error, setError] = useState<string | undefined>(undefined);
    const [showNotice, setShowNotice] = useState(false);

    const startMutation = useMutation({
        mutationFn: async () => {
            return await emissionsApiClient.emissionRecordsGeneratePdfCreate({
                id: props.emissionId,
            });
        },
        onSuccess: (data) => {
            setError(undefined);
            if (data.status === "already_in_progress") {
                setInfo(
                    "PDF export already in progress for this emission — check Data Downloads.",
                );
                return;
            }
            setInfo(undefined);
            setDataDownloadId(data.dataDownloadId);
            setShowNotice(true);
        },
        onError: (err: unknown) => {
            setInfo(undefined);
            setShowNotice(false);
            setError(
                err instanceof Error && err.message
                    ? err.message
                    : "Failed to start PDF export. Try again.",
            );
        },
    });

    const statusQuery = useQuery({
        queryKey: ["emissionPdfStatus", dataDownloadId],
        queryFn: async () => {
            return await downloadsApiClient.datadownloadsDataPackageRetrieve({
                id: dataDownloadId,
            });
        },
        enabled: !!dataDownloadId,
        refetchInterval: (data) =>
            !data || data?.dataDownloadStatus !== "READY" ? 1000 : false,
    });

    const isReady =
        !!dataDownloadId && statusQuery.data?.dataDownloadStatus === "READY";

    const autoOpenedRef = useRef<string | undefined>(undefined);

    const downloadUrlQuery = useQuery({
        queryKey: ["emissionPdfDownloadUrl", dataDownloadId],
        queryFn: async () => {
            const response =
                await downloadsApiClient.datadownloadsDataPackageDownloadUrlRetrieve(
                    { id: dataDownloadId },
                );
            return response.downloadUrl;
        },
        enabled: isReady,
        staleTime: Infinity,
    });

    useEffect(() => {
        if (
            isReady &&
            downloadUrlQuery.data &&
            autoOpenedRef.current !== dataDownloadId
        ) {
            autoOpenedRef.current = dataDownloadId;
            window.open(downloadUrlQuery.data, "_blank", "noopener,noreferrer");
        }
    }, [isReady, downloadUrlQuery.data, dataDownloadId]);

    if (
        !userFlags.includes("enable_download_emission_pdf") ||
        !userHasExportPermissions
    ) {
        return null;
    }

    const className = `${baseClasses} ${props.className ?? "text-slate-500 hover:text-slate-700 hover:bg-slate-100"}`;

    const isProcessing =
        startMutation.isPending ||
        (!!dataDownloadId && statusQuery.data?.dataDownloadStatus !== "READY");

    const popups = (
        <>
            {showNotice && isProcessing && (
                <DismissiblePopup onDismiss={() => setShowNotice(false)}>
                    <p>
                        The PDF is being generated in the background and will be
                        available shortly.
                    </p>
                    <p>
                        If you close this page, it will appear in the{" "}
                        <b>Data Downloads</b> section when ready.
                    </p>
                </DismissiblePopup>
            )}
            {error && (
                <DismissiblePopup
                    tone="error"
                    onDismiss={() => setError(undefined)}
                >
                    {error}
                </DismissiblePopup>
            )}
            {info && (
                <DismissiblePopup onDismiss={() => setInfo(undefined)}>
                    {info}
                </DismissiblePopup>
            )}
        </>
    );

    let trigger: ReactNode;
    if (isReady && downloadUrlQuery.data) {
        trigger = (
            <a
                href={downloadUrlQuery.data}
                target="_blank"
                rel="noreferrer"
                className={`${className} text-emerald-700 hover:text-emerald-900`}
            >
                <FontAwesomeIcon icon={faDownload} className="size-3" />
                Download PDF
            </a>
        );
    } else if (isProcessing) {
        trigger = (
            <span
                className={`${className} text-slate-500`}
                title="PDF is being generated. It will appear here when ready."
            >
                <FontAwesomeIcon icon={faSpinner} className="size-3" spin />
                {startMutation.isPending ? "Starting..." : "Generating PDF..."}
            </span>
        );
    } else {
        trigger = (
            <button
                type="button"
                className={`${className}${error ? " text-red-600" : ""}`}
                title={error ?? info}
                onClick={() => {
                    setError(undefined);
                    setInfo(undefined);
                    startMutation.mutate();
                }}
            >
                <FontAwesomeIcon icon={faDownload} className="size-3" />
                {error
                    ? "Retry PDF export"
                    : info
                      ? "PDF in progress"
                      : "Download as PDF"}
            </button>
        );
    }

    return (
        <span className="relative inline-block">
            {trigger}
            {popups}
        </span>
    );
};
